import { MinusIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { Checkbox } from '@/modules/ui/components/checkbox/Checkbox';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';

interface MessagingLayoutProps {
  sidebar: ReactNode;
  sidebarTitle: string;
  content: ReactNode;
  /**
   * It activates indeterminate state of the checkbox if true
   */
  isAnySelected?: boolean;
  /**
   * Checked state of the "Select All" checkbox
   */
  isAllSelected?: boolean;
  /**
   * If true, the "Select All" checkbox is disabled
   */
  isEmpty?: boolean;
  /**
   * Callback function to handle the "Select All" checkbox
   */
  onSelectAll?: () => void;
}

export const MessagingLayout = ({
  sidebar,
  sidebarTitle,
  content,
  isAllSelected,
  isAnySelected,
  isEmpty,
  onSelectAll,
}: MessagingLayoutProps) => {
  const isSelectAllVisible = Boolean(onSelectAll);
  const checked = isAllSelected && isAnySelected;
  const isIndeterminate = isAnySelected && !isAllSelected;

  const title = <Subheading variant="h5">{sidebarTitle}</Subheading>;

  const cardTitle = isSelectAllVisible ? (
    <div className="flex items-center gap-4">
      <Checkbox
        checked={isIndeterminate ? 'indeterminate' : checked}
        disabled={isEmpty}
        onCheckedChange={onSelectAll}
      >
        {isIndeterminate && <MinusIcon className="size-3.5" />}
      </Checkbox>
      {title}
    </div>
  ) : (
    title
  );

  return (
    <div className="grid h-full grid-cols-1 grid-rows-1 gap-6 lg:grid-cols-[320px_1fr]">
      <div className="flex h-full w-full flex-col lg:w-80 lg:min-w-80 [&>div]:min-h-0 [&>div]:flex-1">
        <Card className="flex h-full flex-col gap-0">
          <CardHeader className="shrink-0 border-b">
            <CardTitle>{cardTitle}</CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-hidden p-0">
            {sidebar}
          </CardContent>
        </Card>
      </div>
      <div className="flex h-full flex-col [&>div]:min-h-0 [&>div]:flex-1">
        <Card className="flex h-full flex-col">
          <CardContent className="flex min-h-0 flex-1 flex-col p-0">
            {content}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
